import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { storeProviderBenefitsStability } from "../../slice/provider_benefit_stabilitySlice";
import { toast } from "react-toastify";

/**
 * NCAF Benefits Readiness Setup
 *
 * Purpose:
 * Simplify the Provider Benefits demo into a guided readiness diagnostic.
 *
 * Developer note:
 * This component can either replace the current provider benefits intake page
 * or be used as a reference patch for the current page.
 */

const sections = [
    {
        key: "provider_basics",
        title: "Provider Basics",
        description:
            "Tell us enough about your organization to understand your workforce size and operating context.",
    },
    {
        key: "workforce_challenges",
        title: "Workforce Stability Challenges",
        description:
            "Select the issues currently affecting your ability to recruit, retain, or support staff.",
    },
    {
        key: "benefits_interest",
        title: "Benefits Interest",
        description:
            "Select the types of support you may want to explore. You do not need to know the final answer yet.",
    },
    {
        key: "current_benefits",
        title: "Current Benefits Status",
        description:
            "Tell us what you currently offer, even if it is limited or informal.",
    },
    {
        key: "readiness",
        title: "Readiness Questions",
        description:
            "These questions help determine whether you are still exploring, organizing, or ready for partner guidance.",
    },
    {
        key: "review_submit",
        title: "Review & Submit",
        description: "Review your answers and submit your readiness intake.",
    },
];

const providerTypeOptions = [
    { value: "center_based", label: "Center-based childcare provider" },
    { value: "family_childcare", label: "Family childcare home" },
    { value: "multi_site", label: "Multi-site provider" },
    { value: "startup_provider", label: "Startup / planning provider" },
    { value: "other", label: "Other" },
];

const staffCountOptions = [
    { value: "1_5", label: "1–5 staff" },
    { value: "6_15", label: "6–15 staff" },
    { value: "16_30", label: "16–30 staff" },
    { value: "31_50", label: "31–50 staff" },
    { value: "51_plus", label: "51+ staff" },
    { value: "not_sure", label: "Not sure yet" },
];

const workforceChallengeOptions = [
    { value: "recruiting_staff", label: "Recruiting qualified staff" },
    { value: "retaining_staff", label: "Retaining existing staff" },
    { value: "staff_absences", label: "Staff absences / coverage gaps" },
    { value: "onboarding_training", label: "Onboarding and training new staff" },
    {
        value: "competing_wages",
        label: "Competing with public schools or larger employers",
    },
    { value: "administrative_overload", label: "Administrative overload" },
    { value: "not_sure", label: "Not sure yet" },
];

const benefitsInterestOptions = [
    { value: "health_coverage", label: "Health coverage guidance" },
    { value: "dental_vision", label: "Dental / vision options" },
    { value: "retirement", label: "Retirement savings options" },
    { value: "financial_wellness", label: "Employee financial wellness" },
    { value: "mental_health_eap", label: "Mental health / EAP resources" },
    { value: "payroll_hr", label: "Payroll / HR coordination" },
    { value: "not_sure", label: "Not sure yet" },
];

const currentBenefitsOptions = [
    { value: "none", label: "No benefits currently" },
    { value: "informal_support", label: "Some informal support" },
    { value: "limited_benefits", label: "Limited benefits offered" },
    { value: "structured_benefits", label: "Structured benefits already offered" },
    { value: "not_sure", label: "Not sure" },
];

const budgetDirectionOptions = [
    { value: "no_budget_yet", label: "No budget identified yet" },
    { value: "exploring_options", label: "Exploring affordable options" },
    {
        value: "some_budget",
        label: "Some employer contribution may be possible",
    },
    { value: "defined_budget", label: "We have a defined budget range" },
    { value: "not_sure", label: "Not sure yet" },
];

const timelineOptions = [
    { value: "immediate", label: "Immediately / within 30 days" },
    { value: "one_to_three_months", label: "1–3 months" },
    { value: "three_to_six_months", label: "3–6 months" },
    { value: "future", label: "Future planning" },
    { value: "not_sure", label: "Not sure yet" },
];

const partnerFollowupOptions = [
    { value: "yes", label: "Yes, I am open to partner follow-up" },
    { value: "later", label: "Maybe later" },
    { value: "education_first", label: "I need education first" },
    { value: "no", label: "No, not at this time" },
];

const STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

const getLabel = (options, value) => {
    return options.find((option) => option.value === value)?.label || value;
};

const getLabels = (options, values) => {
    return values
        ?.map(
            (value) =>
                options.find((option) => option.value === value)?.label || value
        )
        .join(", ");
};

const OptionCardGroup = ({
    label,
    description,
    options,
    value,
    onChange,
    multiple = false,
    error,
}) => {
    return (
        <div className={`mb-4 ${error ? "border border-danger rounded p-3" : ""
            }`}>
            <div className="mb-3">
                <h3 className="fs-6 fw-semibold text-dark">
                    {label}
                </h3>

                {description && (
                    <p className="small text-muted mt-1 mb-0">
                        {description}
                    </p>
                )}

                {error && (
                    <div className="text-danger small mt-2">
                        {error[0]}
                    </div>
                )}
            </div>

            <div className="row g-3">
                {options.map((option) => {
                    const selected = multiple
                        ? value?.includes(option.value)
                        : value === option.value;

                    return (
                        <div key={option.value} className="col-12 col-md-6">
                            <button
                                type="button"
                                onClick={() => onChange(option.value)}
                                className={`w-100 text-start border rounded p-3 bg-white ${selected
                                    ? "border-primary"
                                    : "border-secondary-subtle"
                                    }`}
                                style={{
                                    backgroundColor: selected
                                        ? "#002B4515"
                                        : "#fff",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <div className="d-flex align-items-start gap-3">

                                    {multiple ? (
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            readOnly
                                            className="form-check-input mt-1"
                                            style={{
                                                pointerEvents: "none",
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type="radio"
                                            checked={selected}
                                            readOnly
                                            className="form-check-input mt-1"
                                            style={{
                                                pointerEvents: "none",
                                            }}
                                        />
                                    )}

                                    <span
                                        className="fw-medium"
                                        style={{
                                            color: selected
                                                ? "#002B45"
                                                : "#212529",
                                            fontSize: "14px",
                                        }}
                                    >
                                        {option.label}
                                    </span>
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default function BenefitsStabilityIntake() {
    const dispatch = useDispatch();
    const token = localStorage.getItem("token");
    const [activeSection, setActiveSection] = useState("provider_basics");
    const [showResult, setShowResult] = useState(false);
    const [readinessResult, setReadinessResult] = useState(null);
    const [isAutoFilled, setIsAutoFilled] = useState(false);
    const { userData } = useSelector((state) => state.workerInfo);
    const [validationErrors, setValidationErrors] = useState({});

    console.log("userData", userData);

    const [formData, setFormData] = useState({
        provider_name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        state: "",
        provider_type: "",
        staff_count_range: "",

        workforce_challenges: [],
        benefits_interests: [],

        current_benefits_status: "",
        budget_direction: "",
        target_timeline: "",
        partner_followup_interest: "",

        notes: "",
        consent_acknowledged: false,
    });

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth", // or "auto"
        });
    }, [activeSection]);

    useEffect(() => {
        if (!userData) return;

        setFormData((prev) => ({
            ...prev,
            provider_name: userData.business_name || '',
            contact_name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
            contact_email: userData.email || '',
            contact_phone: userData.phone || '',
            state: userData.states || 'VA',
        }));

        setIsAutoFilled(true);
    }, [userData]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleMultiSelectChange = (field, value) => {
        setFormData((prev) => {
            const currentValues = prev[field] || [];

            if (currentValues.includes(value)) {
                return {
                    ...prev,
                    [field]: currentValues.filter((item) => item !== value),
                };
            }

            return {
                ...prev,
                [field]: [...currentValues, value],
            };
        });
    };

    const sectionKeys = sections.map((section) => section.key);
    const currentIndex = sectionKeys.indexOf(activeSection);
    const currentSection = sections[currentIndex];

    const goNext = () => {
        if (currentIndex < sectionKeys.length - 1) {
            setActiveSection(sectionKeys[currentIndex + 1]);
        }
    };

    const goBack = () => {
        if (currentIndex > 0) {
            setActiveSection(sectionKeys[currentIndex - 1]);
        }
    };

    const navigateToErrorSection = (errors) => {
        const sectionMap = {
            provider_name: "provider_basics",
            contact_name: "provider_basics",
            contact_email: "provider_basics",
            contact_phone: "provider_basics",
            state: "provider_basics",
            provider_type: "provider_basics",
            staff_count_range: "provider_basics",

            workforce_challenges: "workforce_challenges",

            benefits_interests: "benefits_interest",

            current_benefits_status: "current_benefits",

            budget_direction: "readiness",
            target_timeline: "readiness",
            partner_followup_interest: "readiness",
            notes: "readiness",
        };

        const firstErrorField = Object.keys(errors)[0];

        if (firstErrorField && sectionMap[firstErrorField]) {
            setActiveSection(sectionMap[firstErrorField]);
        }
    };

    const handleSubmit = async () => {
        try {
            setValidationErrors({});

            const res = await dispatch(
                storeProviderBenefitsStability({ token, formData })
            ).unwrap();

            toast.success(res?.message || "Submitted successfully");

            setReadinessResult(res?.data);
            setShowResult(true);

        } catch (err) {
            console.error(err);

            const errors = err?.errors || {};

            setValidationErrors(errors);

            toast.error(
                err?.message || "Please correct the highlighted fields."
            );

            navigateToErrorSection(errors);
        }
    };

    const SummaryItem = ({ label, value }) => (
        <div className="col-md-6">
            <div className="bg-white border rounded p-3 h-100">
                <div className="text-muted small">{label}</div>
                <div className="fw-semibold">{value || "-"}</div>
            </div>
        </div>
    );

    const renderSection = () => {
        switch (activeSection) {
            case "provider_basics":
                return (
                    <div className="space-y-5">
                        <div className="row g-3">
                            <div className="col-sm-6">
                                <label className="form-label text-primary mb-1 font-weight-semibold required">Provider Name</label>
                                <input
                                    type="text"
                                    value={formData.provider_name}
                                    className="form-control"
                                    onChange={(e) =>
                                        handleInputChange("provider_name", e.target.value)
                                    }
                                    placeholder="Enter provider or center name"
                                    disabled={isAutoFilled}
                                />
                            </div>
                            <div className="col-sm-6">
                                <label className="form-label text-primary mb-1 font-weight-semibold required">Contact Name</label>
                                <input
                                    type="text"
                                    value={formData.contact_name}
                                    className="form-control"
                                    onChange={(e) =>
                                        handleInputChange("contact_name", e.target.value)
                                    }
                                    placeholder="Primary contact"
                                    disabled={isAutoFilled}
                                />
                            </div>

                            <div className="col-sm-6">
                                <label className="form-label text-primary mb-1 font-weight-semibold required">Email</label>
                                <input
                                    type="email"
                                    value={formData.contact_email}
                                    className="form-control"
                                    onChange={(e) => handleInputChange("contact_email", e.target.value)}
                                    placeholder="email@example.com"
                                    disabled={isAutoFilled}
                                />
                            </div>
                            <div className="col-sm-6">
                                <label className="form-label text-primary mb-1 font-weight-semibold">Phone</label>
                                <input
                                    type="tel"
                                    value={formData.contact_phone}
                                    className="form-control"
                                    onChange={(e) => handleInputChange("contact_phone", e.target.value)}
                                    placeholder="Optional"
                                    disabled={isAutoFilled}
                                />
                            </div>

                            <div className="col-sm-6">
                                <label className="form-label text-primary mb-1 font-weight-semibold required">State</label>
                                <select
                                    value={formData.state}
                                    className="form-control"
                                    onChange={(e) => handleInputChange("state", e.target.value)}
                                    placeholder="Optional"
                                    disabled={isAutoFilled}
                                >
                                    {STATES.map((state) => (
                                        <option key={state.value} value={state.value}>
                                            {state.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                        </div>
                        <div className="mt-4">
                            <OptionCardGroup
                                label="Provider Type"
                                options={providerTypeOptions}
                                value={formData.provider_type}
                                onChange={(value) => handleInputChange("provider_type", value)}
                            />
                        </div>
                        <div className="mt-4">
                            <OptionCardGroup
                                label="Approximate Staff Count"
                                description="Use your best estimate. This does not need to be exact."
                                options={staffCountOptions}
                                value={formData.staff_count_range}
                                onChange={(value) =>
                                    handleInputChange("staff_count_range", value)
                                }
                            />
                        </div>
                    </div>
                );

            case "workforce_challenges":
                return (
                    <OptionCardGroup
                        label="What workforce challenges are you currently facing?"
                        description="Select all that apply."
                        options={workforceChallengeOptions}
                        value={formData.workforce_challenges}
                        multiple
                        onChange={(value) =>
                            handleMultiSelectChange("workforce_challenges", value)
                        }
                    />
                );

            case "benefits_interest":
                return (
                    <OptionCardGroup
                        label="What employee support or benefits are you interested in exploring?"
                        description="Select all that apply. You can choose 'Not sure yet' if you need guidance."
                        options={benefitsInterestOptions}
                        value={formData.benefits_interests}
                        multiple
                        onChange={(value) =>
                            handleMultiSelectChange("benefits_interests", value)
                        }
                    />
                );

            case "current_benefits":
                return (
                    <OptionCardGroup
                        label={
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span className="fw-medium">
                                    What best describes your current benefits status?
                                </span>
                                <span className="text-danger small fw-semibold">
                                    Required
                                </span>
                            </div>
                        }
                        options={currentBenefitsOptions}
                        value={formData.current_benefits_status}
                        onChange={(value) =>
                            handleInputChange("current_benefits_status", value)
                        }
                        error={validationErrors.current_benefits_status}
                    />
                );

            case "readiness":
                return (
                    <div className="space-y-6">
                        <OptionCardGroup
                            // label="What best describes your current budget direction?"
                            label={
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span className="fw-medium">
                                        What best describes your current budget direction
                                    </span>
                                    <span className="text-danger small fw-semibold">
                                        Required
                                    </span>
                                </div>
                            }
                            description="This helps determine whether education, planning, or partner referral is the next best step."
                            options={budgetDirectionOptions}
                            value={formData.budget_direction}
                            onChange={(value) => handleInputChange("budget_direction", value)}
                            error={validationErrors.budget_direction}
                        />

                        <OptionCardGroup
                            // label="When would you like to improve or explore benefits support?"
                            label={
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span className="fw-medium">
                                        When would you like to improve or explore benefits support?
                                    </span>
                                    <span className="text-danger small fw-semibold">
                                        Required
                                    </span>
                                </div>
                            }
                            options={timelineOptions}
                            value={formData.target_timeline}
                            onChange={(value) => handleInputChange("target_timeline", value)}
                            error={validationErrors.target_timeline}
                        />

                        <OptionCardGroup
                            label={
                                <div className="d-flex justify-content-between align-items-center w-100">
                                    <span className="fw-medium">
                                        Are you open to NCAF coordinating a referral to an aligned benefits, payroll, HR, or workforce resource partner?
                                    </span>
                                    <span className="text-danger small fw-semibold">
                                        Required
                                    </span>
                                </div>
                            }
                            // label="Are you open to follow-up from an aligned benefits or workforce resource partner?"
                            options={partnerFollowupOptions}
                            value={formData.partner_followup_interest}
                            onChange={(value) =>
                                handleInputChange("partner_followup_interest", value)
                            }
                            error={validationErrors.partner_followup_interest}
                        />

                        <div className="mb-3">
                            <label className="form-label fw-medium">
                                Additional Notes
                            </label>

                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleInputChange("notes", e.target.value)}
                                className="form-control"
                                rows={4}
                                placeholder="Optional: share anything else about your workforce or benefits goals."
                            />
                        </div>
                    </div>
                );

            case "review_submit":
                return (
                    <div className="d-flex flex-column gap-4">

                        <div className="card bg-light border">
                            <div className="card-body p-4">
                                <h3 className="fs-6 fw-semibold mb-4">
                                    Review Before Submitting
                                </h3>

                                {/* Organization Information */}
                                <div className="mb-4">
                                    <h4 className="fs-6 fw-bold border-bottom pb-2 mb-3">
                                        Organization Information
                                    </h4>

                                    <div className="row gy-2">
                                        <SummaryItem
                                            label="Provider Name"
                                            value={formData.provider_name}
                                        />
                                        <SummaryItem
                                            label="Provider Type"
                                            value={getLabel(providerTypeOptions, formData.provider_type)}
                                        />
                                        <SummaryItem
                                            label="State"
                                            value={formData.state}
                                        />
                                        <SummaryItem
                                            label="Staff Count"
                                            value={getLabel(
                                                staffCountOptions,
                                                formData.staff_count_range
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Contact Information */}
                                <div className="mb-4">
                                    <h4 className="fs-6 fw-bold border-bottom pb-2 mb-3">
                                        Contact Information
                                    </h4>

                                    <div className="row gy-2">
                                        <SummaryItem
                                            label="Contact Name"
                                            value={formData.contact_name}
                                        />
                                        <SummaryItem
                                            label="Email"
                                            value={formData.contact_email}
                                        />
                                        <SummaryItem
                                            label="Phone"
                                            value={formData.contact_phone}
                                        />
                                    </div>
                                </div>

                                {/* Workforce & Benefits Assessment */}
                                <div className="mb-4">
                                    <h4 className="fs-6 fw-bold border-bottom pb-2 mb-3">
                                        Workforce & Benefits Assessment
                                    </h4>

                                    <div className="row gy-2">
                                        <SummaryItem
                                            label="Workforce Challenges"
                                            value={getLabels(
                                                workforceChallengeOptions,
                                                formData.workforce_challenges
                                            )}
                                        />
                                        <SummaryItem
                                            label="Benefits Interests"
                                            value={getLabels(
                                                benefitsInterestOptions,
                                                formData.benefits_interests
                                            )}
                                        />
                                        <SummaryItem
                                            label="Current Benefits"
                                            value={getLabel(
                                                currentBenefitsOptions,
                                                formData.current_benefits_status
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Readiness & Planning */}
                                <div className="mb-4">
                                    <h4 className="fs-6 fw-bold border-bottom pb-2 mb-3">
                                        Readiness & Planning
                                    </h4>

                                    <div className="row gy-2">
                                        <SummaryItem
                                            label="Budget Direction"
                                            value={getLabel(
                                                budgetDirectionOptions,
                                                formData.budget_direction
                                            )}
                                        />
                                        <SummaryItem
                                            label="Timeline"
                                            value={getLabel(
                                                timelineOptions,
                                                formData.target_timeline
                                            )}
                                        />
                                        <SummaryItem
                                            label="Partner Follow-up"
                                            value={getLabel(
                                                partnerFollowupOptions,
                                                formData.partner_followup_interest
                                            )}
                                        />
                                    </div>
                                </div>

                                {formData.notes && (
                                    <div>
                                        <h4 className="fs-6 fw-bold border-bottom pb-2 mb-3">
                                            Additional Notes
                                        </h4>

                                        <div className="bg-white border rounded p-3">
                                            {formData.notes}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-check d-flex align-items-start gap-2">
                            <input
                                type="checkbox"
                                className="form-check-input mt-1"
                                checked={formData.consent_acknowledged}
                                onChange={(e) =>
                                    handleInputChange(
                                        "consent_acknowledged",
                                        e.target.checked
                                    )
                                }
                            />

                            <label className="form-check-label small">
                                I understand that NCAF provides readiness guidance and resource
                                coordination only. NCAF does not provide insurance, legal,
                                payroll, tax, or state licensing determinations.
                            </label>
                        </div>

                        <div>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!formData.consent_acknowledged}
                                className="btn btn-primary px-4 py-2"
                            >
                                Submit Benefits Readiness Intake
                            </button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="col-md-8 col-xl-9 mt-4 mt-md-0">
            <div className="ps-2 ps-lg-4">
                <div className="container py-4">
                    <div
                        className="mx-auto"
                        style={{ maxWidth: "960px" }}
                    >
                        <div className="mb-5">
                            <p
                                className="text-uppercase text-primary fw-bold small mb-2"
                                style={{ letterSpacing: "0.08em" }}
                            >
                                NCAF Provider Support
                            </p>

                            <h1 className="fw-bold">
                                Benefits Readiness Setup
                            </h1>

                            <p className="mt-2">
                                Help NCAF understand your workforce stability and benefits readiness
                                so we can recommend the right next step.
                            </p>

                            <p
                                className="mt-3 border-start border-4 ps-3 fw-bold"
                                style={{ borderColor: "#bfdbfe" }}
                            >
                                NCAF does not act as an insurance broker, benefits administrator,
                                employer, or state licensing authority. This intake helps identify
                                readiness and appropriate resource pathways.
                            </p>
                        </div>
                    </div>
                    {!showResult && (
                        <>
                            <div className="mb-4">
                                <div className="d-flex align-items-center justify-content-between small text-muted">
                                    <span>
                                        Step {currentIndex + 1} of {sections.length}
                                    </span>
                                    <span>{currentSection.title}</span>
                                </div>

                                <div className="progress mt-2" style={{ height: "8px" }}>
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{
                                            width: `${((currentIndex + 1) / sections.length) * 100}%`,
                                        }}
                                        aria-valuenow={((currentIndex + 1) / sections.length) * 100}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    />
                                </div>
                            </div>


                            <div className="card border shadow-sm p-3">
                                <div className="mb-4">
                                    <h2 className="h4 fw-semibold text-dark">
                                        {currentSection.title}
                                    </h2>
                                    <p className="mt-1 text-muted small">
                                        {currentSection.description}
                                    </p>
                                </div>

                                {renderSection()}

                                <div className="d-flex justify-content-between mt-4">
                                    <button
                                        type="button"
                                        onClick={goBack}
                                        disabled={currentIndex === 0}
                                        className="btn btn-outline-secondary"
                                    >
                                        Back
                                    </button>

                                    {activeSection !== "review_submit" && (
                                        <button
                                            type="button"
                                            onClick={goNext}
                                            className="btn btn-primary"
                                        >
                                            Continue
                                        </button>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {showResult && readinessResult && (
                        <div className="mt-4 p-4 border border-primary-subtle rounded bg-primary-subtle">
                            <h2 className="fs-4 fw-bold text-dark mb-3">
                                Your Benefits Readiness Stage
                            </h2>

                            <div className="bg-white border border-primary-subtle rounded p-4">
                                {/* Score + Stage Header */}
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                                    <div>
                                        <p
                                            className="fs-6 text-primary fw-semibold text-uppercase mb-2"
                                            style={{ letterSpacing: "0.05em" }}
                                        >
                                            {readinessResult.readiness_stage}
                                        </p>

                                        <h3 className="fs-5 fw-semibold text-dark mb-0">
                                            {readinessResult.stage_title}
                                        </h3>
                                    </div>

                                    <div
                                        className="text-center border rounded px-4 py-3 bg-light"
                                        style={{ minWidth: "120px" }}
                                    >
                                        <div
                                            className="fw-bold text-primary"
                                            style={{ fontSize: "2rem", lineHeight: 1 }}
                                        >
                                            {readinessResult.readiness_score}/100
                                        </div>
                                        <small className="text-muted">Readiness Score</small>
                                    </div>
                                </div>

                                <p className="mb-4">
                                    {readinessResult.stage_description}
                                </p>

                                <div className="border-top pt-3">
                                    <h4
                                        className="fw-semibold text-dark mb-2"
                                        style={{ fontSize: "0.95rem" }}
                                    >
                                        Recommended Next Step
                                    </h4>

                                    <p className="mb-0">
                                        {readinessResult.recommended_next_step}
                                    </p>
                                </div>

                                {/* NCAF Note */}
                                <div className="mt-4 p-3 rounded bg-light border-start border-4 border-primary">
                                    <h5 className="fw-semibold mb-2">Important</h5>
                                    <p className="mb-0 small text-muted">
                                        {readinessResult.important_note}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
